import { useState } from "react";
import { toast } from "react-toastify";
import {
  FaFileAlt, FaTrash, FaUpload, FaDownload, FaEye,
  FaCloudUploadAlt, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage,
} from "react-icons/fa";
import { uploadMyEmployeeDocument, deleteMyEmployeeDocument } from "../services/profileservice";
import ConfirmModal from "../employee/delete_modal";
import Dropdown from "../modal/dropdown";
import DocumentPreviewModal from "../modal/DocumentPreviewModal";
import { DocumentTypeFormOptions, getDocumentTypeLabel } from "../../constants/constants";
import "./profile.css";

const getFileIcon = (filename = "") => {
  const ext = filename.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return <FaFilePdf />;
  if (["doc", "docx"].includes(ext)) return <FaFileWord />;
  if (["xls", "xlsx"].includes(ext)) return <FaFileExcel />;
  if (["png", "jpg", "jpeg"].includes(ext)) return <FaFileImage />;
  return <FaFileAlt />;
};

export default function MyDocuments({ documents = [], onRefresh }) {
  const [docType, setDocType] = useState(DocumentTypeFormOptions[0]?.value ?? "");
  const [file, setFile] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const handleFileSelect = (f) => {
    if (f) setFile(f);
  };

  // eslint-disable-next-line no-unused-vars
  const clearFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFile(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("document_type", docType);
      formData.append("file", file);
      if (remarks) formData.append("remarks", remarks);

      await uploadMyEmployeeDocument(formData);
      toast.success("Document uploaded successfully");
      setFile(null);
      setRemarks("");
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const confirmDeleteDoc = async (password) => {
    try {
      await deleteMyEmployeeDocument(deleteTarget.id, password);
      toast.success("Document deleted successfully");
      setDeleteTarget(null);
      if (onRefresh) await onRefresh();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || err.message || "Failed to delete document";
      throw new Error(msg);
    }
  };

  return (
    <div className="account-card">
      <h2>My Documents & Certificates</h2>

      {/* UPLOAD FORM — Document Type / Select File / Remarks in one row */}
      <div className="form-section-card">
        <div className="section-title-premium"><FaUpload /> Upload New Document</div>
        <form onSubmit={handleUpload}>
          <div className="grid-3">
            <div className="account-field">
              <label>Document Type</label>
              <Dropdown
                options={DocumentTypeFormOptions}
                value={docType}
                placeholder="Select Document Type"
                onChange={(value) => setDocType(value)}
              />
            </div>

            <div className="account-field">
              <label>Select File</label>
              <label htmlFor="doc-upload-file" className="file-choose-premium">
                <FaCloudUploadAlt className="file-choose-icon" />
                <span className={`file-choose-text ${file ? "has-file" : ""}`}>
                  {file ? file.name : "Choose a file to upload"}
                </span>
              
              </label>
              <input
                type="file"
                id="doc-upload-file"
                className="hidden-file-input"
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              />
            </div>

            <div className="account-field">
              <label>Remarks / Notes</label>
              <input
                type="text"
                value={remarks}
                placeholder="e.g., B.Tech Degree Certificate"
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          </div>

          <button type="submit" className="doc-upload-submit-btn" disabled={uploading}>
            <FaUpload />
            {uploading ? "Uploading…" : "Upload Document"}
          </button>
        </form>
      </div>

      {/* DOCUMENT LIST */}
      <div className="docs-list-title">Uploaded Documents ({documents.length})</div>

      {documents.length === 0 ? (
        <div className="empty-state">
                  <img src="/images/nodocument.svg" alt="No documents" className="empty-img" />
                  <p>No Documents Uploaded</p>
                </div>
      ) : (
        <div className="docs-list">
          {documents.map((doc) => (
            <div key={doc.id} className="doc-item">
              <div className="doc-left">
                <div className="doc-icon-badge">
                  {getFileIcon(doc.original_filename)}
                </div>
                <div className="doc-meta">
                  <div className="doc-type-label">
                    {getDocumentTypeLabel(doc.document_type)}
                  </div>
                  <div className="doc-filename">
                    {doc.original_filename} • {doc.remarks || "No remarks"}
                  </div>
                </div>
              </div>

              <div className="doc-actions">
                {doc.file_url && (
                  <button type="button" onClick={() => setPreviewDoc(doc)} className="doc-view-btn">
                    <FaEye /> Preview
                  </button>
                )}
                {doc.file_url && (
                  <button
                    type="button"
                    className="doc-view-btn"
                    onClick={() => {
                        const url = doc.download_url || doc.file_url;
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = doc.original_filename || "document";
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                    }}
                  >
                    <FaDownload /> Download
                  </button>
                )}
                <button type="button" onClick={() => setDeleteTarget(doc)} className="doc-delete-btn">
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteTarget && (
        <ConfirmModal
          title="Delete Document"
          message={`Are you sure you want to delete "${deleteTarget.original_filename || deleteTarget.document_type || "this document"}"?`}
          confirmText="Delete"
          type="danger"
          onConfirm={confirmDeleteDoc}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* PREVIEW MODAL */}
      <DocumentPreviewModal 
        doc={previewDoc} 
        onClose={() => setPreviewDoc(null)} 
      />
    </div>
  );
}